import sensorService from '../../control-panel/services/sensorService';
import { DEVICE_ACTIONS_PERMISSION } from '../../common/consts/consts';

export const DEVICE_CODE_VALIDATION_DELAY_MS = 400;
export const SCAN_DEVICE_QR_TYPE = 'scanDeviceQr';

const DEVICE_STATUS_DESKTOP_COLUMNS = '1fr 0.5fr 0.6fr 70px 80px 120px 80px 80px 80px';
const DEVICE_STATUS_MOBILE_COLUMNS = '0.5fr 0.8fr 0.5fr 0.3fr 0.2fr 0.2fr';

const SENSOR_DISPLAY_FIELDS = [
	'company_name',
	'site_name',
	'site_country',
	'site_city',
	'site_state',
	'franchise_name',
	'room_name',
];

/**
 * @param {string} raw
 * @returns {string}
 */
export function parseDeviceCodeFromInput(raw) {
	if (raw == null) return '';
	let value = String(raw).trim();
	if (!value) return '';

	try {
		if (/^https?:\/\//i.test(value) || value.startsWith('?')) {
			const queryPart = value.includes('?') ? value.split('?').slice(1).join('?') : value.replace(/^\?/, '');
			const params = new URLSearchParams(queryPart);
			const qrParam = params.get('QR') || params.get('qr') || params.get('deviceCode') || params.get('device_code');
			if (qrParam) value = qrParam;
		}
	} catch (e) {
		// keep original value
	}

	if (value.includes('QR:')) {
		const qrMatch = value.match(/QR:([^&?\s#]+)/i);
		if (qrMatch) value = qrMatch[1];
	}

	try {
		value = decodeURIComponent(value.trim());
	} catch (e) {
		value = value.trim();
	}
	if (value.toUpperCase().startsWith('QR:')) {
		value = value.slice(3).trim();
	}

	return value.trim();
}

/** @param {string} userType */
export function isAdminUserType(userType) {
	return userType === 'admin';
}

/** @param {string} userType */
export function canScanDeviceCode(userType) {
	return userType === 'admin' || userType === 'installer';
}

/** @param {string} deviceType */
export function isPadDeviceType(deviceType) {
	const type = (deviceType || '').toLowerCase();
	return type === 'pad9' || type === 'pad9+';
}

/** @param {string} userType @param {object[]} devices */
export function canShowPadScanColumn(userType, devices) {
	return canScanDeviceCode(userType) && (devices || []).some((d) => isPadDeviceType(d.device_type));
}

/** @param {{ roles?: string[] } | null | undefined} user */
export function canSeeDeviceActions(user) {
	return Array.isArray(user?.roles) && user.roles.includes(DEVICE_ACTIONS_PERMISSION);
}

/** @param {boolean} isMobile @param {boolean} showScanColumn @param {boolean} showActionsColumn */
export function getDeviceStatusGridStyle(isMobile, showScanColumn, showActionsColumn) {
	if (isMobile) {
		return showScanColumn ? `${DEVICE_STATUS_MOBILE_COLUMNS} 0.15fr` : DEVICE_STATUS_MOBILE_COLUMNS;
	}

	const columns = [DEVICE_STATUS_DESKTOP_COLUMNS];
	if (showScanColumn) columns.push('40px');
	if (showActionsColumn) columns.push('80px');
	if (!showScanColumn) columns.push('10px');
	return columns.join(' ');
}

/**
 * @param {object} validation
 * @param {boolean} isAdmin
 * @param {(key: string) => string} t
 */
export function getDuplicateDeviceCodeMessage(validation, isAdmin, t) {
	if (validation.status !== 'duplicate') return '';
	if (isAdmin && validation.existing) {
		const { company_name, site_name, room_name } = validation.existing;
		return t('assigned_to_company_site_room')
			.replace('{company}', company_name || '—')
			.replace('{site}', site_name || '—')
			.replace('{room}', room_name || '—');
	}
	return t('device_code_already_exists');
}

/**
 * @param {Error} err
 * @param {boolean} isAdmin
 * @param {(key: string) => string} t
 */
export function getDeviceCodeConflictErrorMessage(err, isAdmin, t) {
	if (err?.validationCode !== 'device_code_already_exists' && err?.status !== 409) return null;
	return getDuplicateDeviceCodeMessage(
		{ status: 'duplicate', existing: err.validationDetails || {} },
		isAdmin,
		t,
	);
}

const SERVER_CHECK_FAILURE_STATUSES = new Set([500, 502, 503, 504]);

/**
 * A device code is a single token. A URL, a query string, or a space means the
 * QR text was not reduced to the code printed on the device.
 * @param {string} code
 */
export function looksLikeDeviceCode(code) {
	if (!code || code.length > 80) return false;
	return !/[\s/?#]/.test(code) && !/^https?:/i.test(code);
}

/**
 * @param {{ status?: string, normalizedCode?: string, error?: { status?: number } }} validation
 * @param {(key: string) => string} t
 */
export function getDeviceCodeLookupErrorMessage(validation, t) {
	const code = validation?.normalizedCode || '';
	if (code && !looksLikeDeviceCode(code)) {
		const shown = code.length > 80 ? `${code.slice(0, 77)}...` : code;
		return t('device_code_not_a_code').replace('{code}', shown);
	}
	const status = validation?.error?.status;
	if (status === 403) return t('device_code_no_site_access');
	if (status == null || SERVER_CHECK_FAILURE_STATUSES.has(status)) return t('device_code_check_unavailable');
	return t('device_code_lookup_failed');
}

/** @param {string} validationState */
export function isDeviceCodeSubmitAllowed(validationState, hasCode) {
	if (!hasCode) return false;
	if (validationState === 'checking' || validationState === 'error' || validationState === 'duplicate') return false;
	return validationState === 'available' || validationState === 'unchanged';
}

/**
 * @param {string} code
 * @param {number|string|null|undefined} currentSensorId
 * @param {string} currentDeviceCode
 * @param {(deviceCode: string, options?: object) => Promise<object>} getByDeviceCode
 */
export async function validateDeviceCodeAvailability(code, currentSensorId, currentDeviceCode, getByDeviceCode) {
	const normalizedCode = parseDeviceCodeFromInput(code);
	if (!normalizedCode) {
		return { status: 'empty', normalizedCode: '' };
	}

	if (normalizedCode === parseDeviceCodeFromInput(currentDeviceCode)) {
		return { status: 'unchanged', normalizedCode };
	}

	if (!looksLikeDeviceCode(normalizedCode)) {
		return { status: 'error', normalizedCode, error: { reason: 'not_a_code' } };
	}

	try {
		const existing = await getByDeviceCode(normalizedCode, { silent: true });
		if (!existing?.device_code) {
			return { status: 'available', normalizedCode };
		}
		if (currentSensorId != null && existing.id === currentSensorId) {
			return { status: 'available', normalizedCode };
		}
		return {
			status: 'duplicate',
			normalizedCode,
			existing: {
				id: existing.id,
				company_name: existing.company_name,
				site_name: existing.site_name,
				room_name: existing.room_name,
			},
		};
	} catch (err) {
		// GET /sensor/:deviceCode returns 404 when the code is not registered. Assign uses that lookup to accept a new code.
		if (err?.status === 404) {
			return { status: 'available', normalizedCode };
		}
		return { status: 'error', normalizedCode, error: err };
	}
}

/** @param {object} result @param {{ getCode: Function; setCode: Function; setValidationState: Function }} handlers */
export function applyDeviceCodeValidationResult(result, { getCode, setCode, setValidationState }) {
	if (result.status === 'empty') {
		setValidationState('idle');
		return;
	}
	setValidationState(result.status);
	if (result.normalizedCode && result.normalizedCode !== getCode()) {
		setCode(result.normalizedCode);
	}
}

/** @param {object} sensor @param {string} newDeviceCode @param {string} oldDeviceCode */
export function buildDeviceCodeUpdatePayload(sensor, newDeviceCode, oldDeviceCode) {
	const payload = { ...sensor, device_code: newDeviceCode, oldDevice: oldDeviceCode };
	SENSOR_DISPLAY_FIELDS.forEach((field) => delete payload[field]);
	return payload;
}

/** @param {string} deviceId @param {string} tooltipText */
export function buildScanDeviceRowAction(deviceId, tooltipText) {
	return {
		icon: 'qr_code',
		type: SCAN_DEVICE_QR_TYPE,
		deviceCode: deviceId,
		tooltip: { text: tooltipText },
	};
}

/**
 * @param {object} row
 * @param {object} device
 * @param {boolean} showScanColumn
 * @param {string} tooltipText
 */
export function appendScanDeviceCell(row, device, showScanColumn, tooltipText) {
	if (!showScanColumn) return row;
	const { actions, ...rest } = row;
	return {
		...rest,
		scan_device: isPadDeviceType(device.device_type)
			? buildScanDeviceRowAction(device.device_id, tooltipText)
			: '',
		...(actions !== undefined ? { actions } : {}),
	};
}

/**
 * @param {string} oldDeviceCode
 * @param {string} newDeviceCode
 * @param {{ getByDeviceCode: Function; updateSensor: Function }} sensorApi
 * @param {object|null} [sensor]
 */
export async function replaceSensorDeviceCode(oldDeviceCode, newDeviceCode, sensorApi, sensor = null) {
	const resolvedSensor = sensor || await sensorApi.getByDeviceCode(oldDeviceCode, { silent: true });
	const payload = buildDeviceCodeUpdatePayload(resolvedSensor, newDeviceCode, oldDeviceCode);
	await sensorApi.updateSensor(payload);
	return payload;
}

/** Shared Vue mixin for device-code validation in modals. */
export const deviceCodeValidationMixin = {
	data() {
		return {
			validationState: 'idle',
			validationResult: null,
			validationTimer: null,
			_lastValidatedNormalizedCode: '',
		};
	},
	computed: {
		deviceCodeDuplicateMessage() {
			const isAdmin = typeof this.isAdminUser === 'boolean'
				? this.isAdminUser
				: isAdminUserType(this.$store?.getters?.loggedUser?.type);
			return getDuplicateDeviceCodeMessage(this.validationResult || {}, isAdmin, (key) => this.$t(key));
		},
		deviceCodeErrorMessage() {
			return getDeviceCodeLookupErrorMessage(this.validationResult || {}, (key) => this.$t(key));
		},
	},
	beforeDestroy() {
		this.clearDeviceCodeValidationTimer();
	},
	methods: {
		clearDeviceCodeValidationTimer() {
			if (this.validationTimer) {
				clearTimeout(this.validationTimer);
				this.validationTimer = null;
			}
		},
		scheduleDeviceCodeValidation(callback) {
			this.clearDeviceCodeValidationTimer();
			this.validationTimer = setTimeout(callback, DEVICE_CODE_VALIDATION_DELAY_MS);
		},
		async validateAndApplyDeviceCode(code, currentSensorId, currentDeviceCode, getCode, setCode) {
			const normalizedCode = parseDeviceCodeFromInput(code);
			if (
				normalizedCode
				&& normalizedCode === this._lastValidatedNormalizedCode
				&& ['available', 'unchanged', 'duplicate', 'error'].includes(this.validationState)
			) {
				return;
			}

			this.validationState = 'checking';
			const result = await validateDeviceCodeAvailability(
				code,
				currentSensorId,
				currentDeviceCode,
				(deviceCode, options) => sensorService.getByDeviceCode(deviceCode, options),
			);
			this._lastValidatedNormalizedCode = result.normalizedCode || normalizedCode;
			this.validationResult = result;
			applyDeviceCodeValidationResult(result, {
				getCode,
				setCode,
				setValidationState: (state) => { this.validationState = state; },
			});
		},
	},
};
